import {useRef, useCallback, useMemo} from 'react';
import {getWatchedPercentage} from '../utils/getWatchedPercentage';

interface UseVerticalVideoPlayerHandlersProps {
  tracker: any;
  getContentProperties: () => any;
  refCurrentDuration: React.MutableRefObject<number>;
  watchTime: number;
  setWatchTime: React.Dispatch<React.SetStateAction<number>>;
  lastTimeRef: React.MutableRefObject<number>;
  news: any;
}

export function useVerticalVideoPlayerTracking({
  tracker,
  getContentProperties,
  refCurrentDuration,
  watchTime,
  setWatchTime,
  lastTimeRef,
  news,
}: UseVerticalVideoPlayerHandlersProps) {
  const watchedPercentage = useMemo(
    () => getWatchedPercentage(watchTime, refCurrentDuration.current),
    [watchTime, refCurrentDuration.current],
  );

  // Flag pour ignorer les events pause/resume déclenchés par la fin de vidéo
  const isEndingRef = useRef(false);

  const trackEnd = useCallback(() => {
    if (!news) return;
    isEndingRef.current = true;
    tracker.player.ended({
      ...getContentProperties(),
      watchedPercentage,
      duration: refCurrentDuration.current,
    });
  }, [
    news,
    tracker,
    getContentProperties,
    watchedPercentage,
    refCurrentDuration,
  ]);

  const onTimeUpdate = useCallback(
    (seconds: number) => {
      const delta = seconds - lastTimeRef.current;
      if (delta > 0 && delta < 2) {
        setWatchTime(prev => prev + delta);
      }
      lastTimeRef.current = seconds;
    },
    [setWatchTime, lastTimeRef],
  );

  const onPlayEnd = useCallback(() => {
    trackEnd();
    setWatchTime(0);
  }, [trackEnd, setWatchTime]);

  const isAtEnd = (
    event?: React.SyntheticEvent<HTMLVideoElement> | Event | undefined,
  ) => {
    const video =
      event && 'target' in event
        ? (event.target as HTMLVideoElement)
        : undefined;
    if (!video) return false;
    return video.currentTime >= video.duration - 0.5;
  };

  const onVideoPlay = useCallback(
    (isPaused: boolean, event?: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event?.currentTarget;

      if (video && video.currentTime < 1) {
        // Nouveau départ : reset le flag et ne pas faire de resumed
        isEndingRef.current = false;
        tracker.player.started({
          ...getContentProperties(),
          watchedPercentage: 0,
          duration: refCurrentDuration.current,
        });
        setWatchTime(0);
        return;
      }

      if (isEndingRef.current) {
        isEndingRef.current = false;
        return;
      }

      if (isAtEnd(event)) return;
      if (!isPaused) return;

      tracker.player.resumed({
        ...getContentProperties(),
        watchedPercentage,
        duration: refCurrentDuration.current,
      });
    },
    [
      tracker,
      getContentProperties,
      watchedPercentage,
      refCurrentDuration,
      setWatchTime,
    ],
  );

  const onVideoPause = useCallback(
    (event?: React.SyntheticEvent<HTMLVideoElement>) => {
      if (isEndingRef.current) {
        isEndingRef.current = false;
        return;
      }
      if (isAtEnd(event)) return;
      tracker.player.paused({
        ...getContentProperties(),
        watchedPercentage,
        duration: refCurrentDuration.current,
      });
    },
    [tracker, getContentProperties, watchedPercentage, refCurrentDuration],
  );

  return {
    onTimeUpdate,
    onPlayEnd,
    onVideoPlay,
    onVideoPause,
    watchedPercentage,
    trackEnd,
  };
}
